/**
 * Знак бренда — шапка, страница входа, переключатель брендов (docs/multi-brand.md §6).
 *
 * Чем рисуется знак, решает ТЕМА бренда, а не его id: тема — это набор токенов и знак к нему,
 * а новый бренд с существующей темой — только конфиг, без правки кода.
 *
 *   • `geoboost` — знак-сетка `Logo` и вордмарк `.wm`: разметка и классы прежней шапки
 *     (docs/ui-design.md §5.1), двухцветный `geo<b>boost</b>`, если вордмарк — «GeoBoost».
 *     Вордмарк на `--sans`, как и был;
 *   • `strategai` — слэш-локап по брендбуку StrategAI v3.7 (§3.1, Brand Book «/strategai»):
 *     оранжевый `/` и вордмарк строчными, как задан в конфиге, JetBrains Mono 700
 *     (`--logo-font`), белым на тёмной шапке. Слэш — символ того же шрифта, а не рисунок:
 *     так знак собирается из текста конфига, и его не перерисовывают руками под каждый бренд;
 *   • `strategai-light` — тот же локап на светлой шапке (AI Lab, `/ai_lab`): слэш оранжевый
 *     (`--violet`), текст тёмный (`--ink` темы). Разметка одна на обе темы StrategAI — цвета
 *     несут токены, и второй вариант знака завёл бы второе место, где его правят.
 *
 * Цвета знак берёт из токенов того элемента, внутри которого стоит. Поэтому плашка
 * переключателя с `data-theme` своего бренда показывает знак в его собственной палитре,
 * какой бы ни была тема страницы.
 *
 * `textClassName` — класс текстовой части знака: шапка гасит её стилем на узком окне
 * (лестница подписей, `Shell.module.css`), и знак остаётся символом — сеткой или слэшем.
 */

import type { JSX } from 'react';
import type { BrandLook } from '../brand.tsx';
import styles from './BrandMark.module.css';
import { Logo } from './icons.tsx';

export type BrandMarkSize = 'header' | 'login' | 'menu';

/** Размер знака-сетки GeoBoost: в шапке 24 — как был, на входе 28 — как был. */
const LOGO_PX: Readonly<Record<BrandMarkSize, number>> = { header: 24, login: 28, menu: 22 };

export interface BrandMarkProps {
  readonly look: BrandLook;
  readonly size?: BrandMarkSize;
  readonly textClassName?: string | undefined;
}

/** Двухцветный вордмарк GeoBoost — только для самого GeoBoost, а не для любой темы geoboost. */
const isGeoBoost = (wordmark: string): boolean => wordmark.toLowerCase() === 'geoboost';

export function BrandMark({ look, size = 'header', textClassName }: BrandMarkProps): JSX.Element {
  switch (look.theme) {
    case 'geoboost':
      return (
        <>
          <Logo size={LOGO_PX[size]} />
          <span className={textClassName === undefined ? 'wm' : `wm ${textClassName}`}>
            {isGeoBoost(look.wordmark) ? (
              <>
                geo<b>boost</b>
              </>
            ) : (
              look.wordmark
            )}
          </span>
        </>
      );
    case 'strategai':
    case 'strategai-light':
      return (
        <span className={`${styles['lockup']} ${styles[size]}`} data-testid="slash-lockup">
          <span className={styles['slash']}>/</span>
          <span className={textClassName}>{look.wordmark}</span>
        </span>
      );
    default: {
      // Новая тема в контрактах без своего знака — ошибка компиляции здесь.
      const never: never = look.theme;
      return never;
    }
  }
}
